import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadGateways, loadIdusForGateways, selectGateways, sendVrvCommand, sendBulkVrvCommand, createVrvSchedule, loadVrvSchedules, deleteVrvSchedule, toggleVrvSchedule, updateVrvSchedule } from '../redux/action/vrv';
import { fetchIdus } from './api';
import GatewaySelector from './components/GatewaySelector';
import IduTable from './components/IduTable';
import CommandModal from './components/CommandModal';
import BulkCommandModal from './components/BulkCommandModal';
import VRVScheduleModal from './components/VRVScheduleModal';
import VRVScheduleList from './components/VRVScheduleList';

const RefreshIcon = ({ size = 14 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

function VRVController() {
  const { gateways, selectedGateways, idus, loadingIdus, commandLoading, bulkCommandLoading, schedules, loadingSchedules } = useSelector(s => s.vrv);
  const houseData = useSelector(s => s.houseData);
  const login = useSelector(s => s.login);
  const [modal, setModal] = useState({ open: false, idu: null, pointName: null, currentValue: null });
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleIdus, setScheduleIdus] = useState([]);
  const [activeTab, setActiveTab] = useState('idus');
  const [highlightSelector, setHighlightSelector] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIduIds, setSelectedIduIds] = useState([]);

  const selectedGatewaysRef = useRef(selectedGateways);
  const busyRef = useRef(false);
  useEffect(() => { selectedGatewaysRef.current = selectedGateways; }, [selectedGateways]);
  useEffect(() => { busyRef.current = modal.open || bulkModalOpen || commandLoading; }, [modal.open, bulkModalOpen, commandLoading]);

  useEffect(() => { loadGateways(); }, []);

  // Clear selection when gateway changes
  useEffect(() => { setSelectedIduIds([]); }, [selectedGateways?.join(',')]);

  useEffect(() => {
    if (!selectedGateways?.length) return;
    const id = setInterval(() => {
      if (selectedGatewaysRef.current?.length && !busyRef.current) {
        loadIdusForGateways(selectedGatewaysRef.current, { silent: true });
      }
    }, 30000);
    return () => clearInterval(id);
  }, [selectedGateways?.join(',')]);

  const handleGatewayChange = (vrvGatewayIds) => {
    setHighlightSelector(false);
    selectGateways(vrvGatewayIds);
  };

  const handleSelctorClick = () => {
    setHighlightSelector(false);
    loadGateways();
  };

  const handleCellClick = (idu, pointName, currentValue) => {
    setModal({ open: true, idu, pointName, currentValue });
  };

  const closeModal = () => setModal({ open: false, idu: null, pointName: null, currentValue: null });

  const handleCommand = async (params) => {
    const result = await sendVrvCommand(params);
    if (result?.success) closeModal();
  };

  const handleSelectChange = (iduDeviceId, checked) => {
    setSelectedIduIds(prev =>
      checked ? [...prev, iduDeviceId] : prev.filter(id => id !== iduDeviceId)
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIduIds(prev => {
        const existing = new Set(prev);
        filteredIdus.forEach(idu => existing.add(idu.iduDeviceId));
        return [...existing];
      });
    } else {
      const filtered = new Set(filteredIdus.map(idu => idu.iduDeviceId));
      setSelectedIduIds(prev => prev.filter(id => !filtered.has(id)));
    }
  };

  const handleBulkCommand = async ({ pointName, value, maxAttempts }) => {
    const result = await sendBulkVrvCommand({ selectedIduIds, pointName, value, maxAttempts });
    if (result?.success) {
      setBulkModalOpen(false);
      setSelectedIduIds([]);
    }
  };

  // ─── Schedule tab ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (activeTab === 'schedules' && houseData?._id) {
      loadVrvSchedules("69fa5aef8f7c408c6e9299e0");
    }
  }, [activeTab, houseData?._id]);

  useEffect(() => {
    if (!scheduleModalOpen || !gateways.length) return;
    const allIds = gateways.map(g => g.vrvGatewayId).join(',');
    const floorMap = new Map(gateways.map(g => [g.vrvGatewayId, g.name]));
    fetchIdus(allIds).then(res => {
      const data = res?.data || [];
      setScheduleIdus(data.map(idu => ({
        ...idu,
        latestStatus: { ...idu.latestStatus, floorName: floorMap.get(idu.vrvGatewayId) || idu.vrvGatewayId },
      })));
    }).catch(() => setScheduleIdus(idus));
  }, [scheduleModalOpen]);

  const handleOpenCreate = () => { setEditingSchedule(null); setScheduleModalOpen(true); };
  const handleEditSchedule = (s) => { setEditingSchedule(s); setScheduleModalOpen(true); };
  const handleDeleteSchedule = (id) => deleteVrvSchedule(id);
  const handleToggleSchedule = (id, enabled) => toggleVrvSchedule(id, enabled);

  const buildScheduleActions = (data) => ([{
    action: 'VRV/SinglePointBulkCommand',
    payload: {
      devices: data.selectedIduIds,
      pointName: data.pointName,
      value: data.value,
      controllerType: 'centralSchedule',
      controllerId: 'centralSchedule',
      controllerDetails: { userId: login?.profile?._id },
    },
  }]);

  const handleScheduleSubmit = async (data) => {
    const base = {
      name: data.name,
      house: "69fa5aef8f7c408c6e9299e0",
      type: data.type,
      expression: data.expression,
      enabled: true,
      actions: buildScheduleActions(data),
      tags: [],
    };
    setScheduleLoading(true);
    let result;
    if (editingSchedule) {
      result = await updateVrvSchedule({ ...base, scheduleId: editingSchedule._id });
    } else {
      result = await createVrvSchedule(base);
    }
    setScheduleLoading(false);
    if (result?.success) { setScheduleModalOpen(false); setEditingSchedule(null); }
  };

  const gatewayIdus = idus?.filter(item => selectedGateways?.includes(item?.vrvGatewayId)) ?? [];
  const q = search.trim().toLowerCase();
  const filteredIdus = q
    ? gatewayIdus.filter(item =>
        item?.name?.toLowerCase().includes(q) ||
        item?.iduDeviceId?.toLowerCase().includes(q)
      )
    : gatewayIdus;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', minHeight: '75vh', fontFamily: 'Manrope' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: '4vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>
            Floors
          </span>
          <GatewaySelector
            gateways={gateways}
            selected={selectedGateways}
            onChange={handleGatewayChange}
            onFocus={handleSelctorClick}
            highlight={highlightSelector}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none', display: 'flex' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search IDU…"
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '6px 12px 6px 30px',
                fontSize: 13,
                fontFamily: 'Manrope',
                outline: 'none',
                width: 220,
                color: '#374151',
              }}
            />
          </div>
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3, gap: 2 }}>
            {[{ key: 'idus', label: 'IDU Control' }, { key: 'schedules', label: 'Schedules' }].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '5px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'Manrope', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                  background: activeTab === tab.key ? '#fff' : 'transparent',
                  color: activeTab === tab.key ? '#111827' : '#6b7280',
                  boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              if (!selectedGateways?.length) { setHighlightSelector(true); return; }
              if (refreshCooldown) return;
              selectGateways(selectedGateways);
              setRefreshCooldown(true);
              setTimeout(() => setRefreshCooldown(false), 5000);
            }}
            disabled={loadingIdus}
            title={refreshCooldown ? 'Please wait 5s before refreshing again' : 'Refresh'}
            style={{
              background: 'none',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: loadingIdus || !selectedGateways?.length || refreshCooldown ? 'not-allowed' : 'pointer',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              opacity: loadingIdus || !selectedGateways?.length || refreshCooldown ? 0.5 : 1,
              gap: 6,
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            <RefreshIcon size={14} /> Refresh
          </button>
        </div>
      </div>

      {activeTab === 'idus' && (
        <>
          {selectedIduIds.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
              padding: '10px 16px', background: 'rgba(32,80,255,0.05)',
              border: '1px solid rgba(32,80,255,0.15)', borderRadius: 8,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#2050ff' }}>
                {selectedIduIds.length} IDU{selectedIduIds.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setBulkModalOpen(true)}
                style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(32,80,255,0.85)', color: '#fff', fontFamily: 'Manrope', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Send Bulk Command
              </button>
              <button
                onClick={() => setSelectedIduIds([])}
                style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', color: '#6b7280', fontFamily: 'Manrope', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          )}
          <IduTable
            idus={filteredIdus}
            loading={loadingIdus}
            onCellClick={handleCellClick}
            isGatewaySelected={!!selectedGateways?.length}
            selectedIds={selectedIduIds}
            onSelectChange={handleSelectChange}
            onSelectAll={handleSelectAll}
          />
        </>
      )}

      {activeTab === 'schedules' && (
        <VRVScheduleList
          schedules={schedules}
          loading={loadingSchedules}
          onCreateSchedule={handleOpenCreate}
          onEditSchedule={handleEditSchedule}
          onDeleteSchedule={handleDeleteSchedule}
          onToggleSchedule={handleToggleSchedule}
        />
      )}

      {modal.open && modal.idu && (
        <CommandModal
          open={modal.open}
          onClose={closeModal}
          idu={modal.idu}
          pointName={modal.pointName}
          currentValue={modal.currentValue}
          onSubmit={handleCommand}
          loading={commandLoading}
        />
      )}

      <BulkCommandModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        selectedCount={selectedIduIds.length}
        onSubmit={handleBulkCommand}
        loading={bulkCommandLoading}
      />

      <VRVScheduleModal
        open={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); setEditingSchedule(null); }}
        idus={scheduleIdus}
        gateways={gateways}
        editSchedule={editingSchedule}
        onSubmit={handleScheduleSubmit}
        loading={scheduleLoading}
      />
    </div>
  );
}

export default VRVController;
